import React from 'react';
import type { WorkflowPlan } from '../../../types/workflow';
import { shortDigest, type WorkflowErrorInfo } from '../../../utils/workflowDsl';
import { Banner, Pill } from './WorkflowFormControls';
import { DefValue } from './WorkflowDefinitionView';
import WorkflowModal from './WorkflowModal';

export interface WorkflowPlanModalProps {
  open: boolean;
  loading: boolean;
  applying: boolean;
  plan: WorkflowPlan | null;
  error: WorkflowErrorInfo | null;
  baseDigest: string | null;
  comment: string;
  onCommentChange: (next: string) => void;
  onApply: () => void;
  onClose: () => void;
}

/**
 * Всё, что приходит из ответа сухого прогона, — чужие данные, и рисовать их
 * как есть нельзя (AIT-296). Объект, попавший в текстовый список, роняет React
 * («Objects are not valid as a React child»), а падение здесь означает не
 * кривую строчку, а белый экран на всей вкладке: модалка живёт внутри панели,
 * и её выброс уносит поддерево целиком.
 */
function renderUnknownList(items: unknown[]): string[] {
  const readable = (value: unknown): string => {
    if (Array.isArray(value)) return value.map(readable).join(', ');
    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, nested]) => `${key.replace(/[_-]+/g, ' ')}: ${readable(nested)}`)
        .join(' · ');
    }
    return String(value ?? '—');
  };
  return items.map(readable);
}

const WorkflowPlanModal: React.FC<WorkflowPlanModalProps> = ({
  open,
  loading,
  applying,
  plan,
  error,
  baseDigest,
  comment,
  onCommentChange,
  onApply,
  onClose,
}) => {
  const unresolved = Array.isArray(plan?.unresolved_refs) ? plan!.unresolved_refs : [];
  // Сервер присылал и `null` в списке изменений: строка таблицы разбирала его
  // как объект и падала на первом же поле.
  const diff = Array.isArray(plan?.diff) ? plan!.diff : [];
  const warnings = Array.isArray(plan?.warnings) ? plan!.warnings : [];
  const canApply = !!plan && plan.would_change && unresolved.length === 0 && !loading && !applying && !error;
  const diffExtraKeys = (entry: unknown) =>
    entry && typeof entry === 'object' && !Array.isArray(entry)
      ? Object.keys(entry as Record<string, unknown>).filter((k) => k !== 'section' && k !== 'operation')
      : [];

  return (
    <WorkflowModal
      open={open}
      title="Save workflow"
      icon="fa-save"
      onClose={applying ? () => undefined : onClose}
      wide
      footer={
        <>
          <span className="cwf-muted cwf-modal__meta">
            base_revision <code title={baseDigest || ''}>{shortDigest(baseDigest)}</code>
          </span>
          <button type="button" className="cwf-btn" onClick={onClose} disabled={applying}>
            Cancel
          </button>
          <button type="button" className="cwf-btn cwf-btn--primary" onClick={onApply} disabled={!canApply} title={!plan?.would_change ? 'Nothing would change' : undefined}>
            {applying ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Applying…
              </>
            ) : (
              <>
                <i className="fas fa-check" aria-hidden="true" /> Apply (dry_run: false)
              </>
            )}
          </button>
        </>
      }
    >
      <p className="cwf-note">
        Step 1 is a dry run (<code>dry_run: true</code>) — nothing is written upstream. Review the plan, then apply. There is no partial apply: the whole set
        is validated and either everything lands or nothing does.
      </p>

      {loading ? (
        <div className="cwf-loading">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Running the dry run…
        </div>
      ) : null}

      {error ? (
        <Banner
          tone="error"
          title={
            error.status === 409
              ? 'Configuration changed while you were editing (409)'
              : error.status === 403
                ? 'Section is closed for writing (403)'
                : error.status === 422
                  ? 'The workflow was rejected (422)'
                  : error.status === 502
                    ? 'Upstream failed to write (502)'
                    : `Save failed${error.status ? ` (${error.status})` : ''}`
          }
          details={error.details}
        >
          <p>{error.message}</p>
          {error.currentRevision ? (
            <p>
              Server revision is now <code>{shortDigest(error.currentRevision, 16)}</code>. Reload the workflow and re-apply your edits.
            </p>
          ) : null}
        </Banner>
      ) : null}

      {plan ? (
        <div className="cwf-plan">
          <div className="cwf-plan__summary">
            <Pill tone={plan.would_change ? 'accent' : 'neutral'}>{plan.would_change ? 'would change' : 'no changes'}</Pill>
            <span className="cwf-muted">
              current_revision <code title={plan.current_revision}>{shortDigest(plan.current_revision)}</code>
            </span>
            {plan.current_revision && baseDigest && plan.current_revision !== baseDigest ? (
              <Pill tone="warn" title="The dry run reports a different revision than the one you read.">
                revision differs
              </Pill>
            ) : null}
          </div>

          <h4>Diff</h4>
          {diff.length ? (
            <table className="cwf-table">
              <thead>
                <tr>
                  <th>section</th>
                  <th>operation</th>
                  <th>details</th>
                </tr>
              </thead>
              <tbody>
                {diff.map((raw, i) => {
                  const d = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
                  const keys = diffExtraKeys(raw);
                  // Запись, которую нечем разобрать, показывается как есть, а не
                  // выбрасывается: пропавшая строка диффа хуже некрасивой.
                  const opaque = !raw || typeof raw !== 'object' || Array.isArray(raw);
                  return (
                    <tr key={i}>
                      <td>
                        <code>{typeof d.section === 'string' ? d.section : '—'}</code>
                      </td>
                      <td>
                        {typeof d.operation === 'string' ? (
                          <Pill tone={d.operation === 'delete' ? 'danger' : d.operation === 'create' ? 'ok' : 'accent'}>{d.operation}</Pill>
                        ) : (
                          <span className="cwf-muted">—</span>
                        )}
                      </td>
                      <td>
                        {opaque ? (
                          <DefValue value={raw} />
                        ) : keys.length ? (
                          <div className="cwf-plan__details">
                            {keys.map((key) => (
                              <div key={key} className="cwf-plan__detail">
                                <span>{key.replace(/[_-]+/g, ' ')}</span>
                                <DefValue value={d[key]} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="cwf-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="cwf-note">Empty diff.</p>
          )}

          {warnings.length ? <Banner tone="warn" title="Warnings" details={renderUnknownList(warnings)} /> : null}
          {unresolved.length ? (
            <Banner tone="error" title="Unresolved references — apply is blocked" details={renderUnknownList(unresolved)} />
          ) : null}
          {plan.ref_resolutions && plan.ref_resolutions.length ? (
            <details className="cwf-details">
              <summary>ref_resolutions ({plan.ref_resolutions.length})</summary>
              <div className="cwf-details__body"><DefValue value={plan.ref_resolutions} /></div>
            </details>
          ) : null}
          {plan.apply_order && plan.apply_order.length ? (
            <details className="cwf-details">
              <summary>apply_order ({plan.apply_order.length})</summary>
              <div className="cwf-details__body"><DefValue value={plan.apply_order} /></div>
            </details>
          ) : null}

          <label className="cwf-field">
            <span className="cwf-field__label">Comment (goes to the audit log)</span>
            <input className="cwf-input" value={comment} onChange={(e) => onCommentChange(e.target.value)} placeholder="Why this change?" disabled={applying} />
          </label>
        </div>
      ) : null}
    </WorkflowModal>
  );
};

export default WorkflowPlanModal;
